import React, { useState, useEffect, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { auth, db } from './firebase';
import { onAuthStateChanged, signOut, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from "firebase/auth";
import { doc, getDoc, setDoc, increment, collection, query, where, getDocs } from "firebase/firestore";

// Páginas públicas (carga inmediata)
import Home from './pages/Home';
import DetallePropiedad from './pages/DetallePropiedad';
import CorredorDetalle from './pages/CorredorDetalle';
import Login from './pages/Login';
import QuienesSomos from './pages/QuienesSomos';
import VenderPropiedad from './pages/VenderPropiedad';
import ComprarPropiedad from './pages/ComprarPropiedad';
import CancelarBusqueda from './pages/CancelarBusqueda';
import CancelarRemates from './pages/CancelarRemates';
import Remates from './pages/Remates';
import RemateDetalle from './pages/RemateDetalle';
import Alquileres from './pages/Alquileres';
import CalculadoraAlquileres from './pages/CalculadoraAlquileres';
import { useConfigSitio } from './hooks/useConfigSitio';

// Paneles y estadísticas (carga diferida — traen xlsx/recharts/heic2any, solo
// se descargan cuando alguien entra a esas páginas). Aliviana el bundle público.
const PanelPropiedades    = lazy(() => import('./pages/PanelPropiedades'));
const PanelAdmin          = lazy(() => import('./pages/PanelAdmin'));
const PanelAgente         = lazy(() => import('./pages/PanelAgente'));
const Estadisticas        = lazy(() => import('./pages/Estadisticas'));
const EstadisticasGenerales = lazy(() => import('./pages/EstadisticasGenerales'));
const EstadisticasPropietario = lazy(() => import('./pages/EstadisticasPropietario'));
const ValidarImportacion  = lazy(() => import('./pages/ValidarImportacion'));
const GestionCuotas       = lazy(() => import('./pages/GestionCuotas'));

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
    if (typeof window.fbq === 'function') {
      window.fbq('track', 'PageView');
    }
  }, [location]);
  return null;
}

export default function App() {
  const config = useConfigSitio();
  const [user, setUser] = useState(null);
  const [usuario, setUsuario] = useState(null);
  const [verificando, setVerificando] = useState(true);
  const [hayRemates, setHayRemates] = useState(false);

  // ── Cambiar contraseña ──
  const [modalClave, setModalClave] = useState(false);
  const [claveActual, setClaveActual] = useState('');
  const [claveNueva, setClaveNueva] = useState('');
  const [claveConfirm, setClaveConfirm] = useState('');
  const [claveLoading, setClaveLoading] = useState(false);
  const [claveError, setClaveError] = useState(null);
  const [claveExito, setClaveExito] = useState(false);

  useEffect(() => {
    return onAuthStateChanged(auth, async (u) => {
      setUser(u);
      if (u) {
        // Detectar nueva sesión (se borra al cerrar la pestaña, no en recargas)
        const sessionKey = `fp_sess_${u.uid}`;
        const esNuevaSesion = !sessionStorage.getItem(sessionKey);

        const usuSnap = await getDoc(doc(db, 'usuarios', u.uid));
        if (usuSnap.exists()) {
          const datosUsu = usuSnap.data();
          setUsuario(datosUsu);
          // Si NO es corredor (admin/agente) y tiene perfil de vendedor, marcarlo
          // para que no aparezca en los listados públicos (Vender / Quiénes Somos),
          // donde no se puede leer 'usuarios' para filtrar por rol.
          if (datosUsu.rol && datosUsu.rol !== 'corredor') {
            setDoc(doc(db, 'vendedores', u.uid), { ocultarEnPublico: true }, { merge: true }).catch(() => {});
          }
          if (esNuevaSesion) {
            sessionStorage.setItem(sessionKey, '1');
            setDoc(doc(db, 'usuarios', u.uid), {
              ultimoLogin: new Date(),
              cantidadLogins: increment(1),
            }, { merge: true });
          }
        } else {
          const vendSnap = await getDoc(doc(db, 'vendedores', u.uid));
          if (vendSnap.exists()) {
            const data = { rol: 'corredor', nombre: vendSnap.data()?.nombre || '', activo: true };
            await setDoc(doc(db, 'usuarios', u.uid), data, { merge: true });
            setUsuario(data);
            if (esNuevaSesion) {
              sessionStorage.setItem(sessionKey, '1');
              setDoc(doc(db, 'usuarios', u.uid), {
                ultimoLogin: new Date(),
                cantidadLogins: increment(1),
              }, { merge: true });
            }
          } else {
            // Usuario eliminado del sistema: cerrar sesión automáticamente
            await signOut(auth);
          }
        }
      } else {
        setUsuario(null);
      }
      setVerificando(false);
    });
  }, []);

  // ── ¿Hay remates futuros publicados? (para mostrar/ocultar el ítem del navbar) ──
  useEffect(() => {
    const desde = new Date(); desde.setHours(desde.getHours() - 12);
    getDocs(query(collection(db, 'remates'), where('publicada', '==', true)))
      .then(snap => {
        const hay = snap.docs.some(d => {
          const data = d.data();
          if (data.eliminado) return false;
          const f = data.fechaHora?.toDate ? data.fechaHora.toDate() : new Date(data.fechaHora);
          return f && !isNaN(f.getTime()) && f >= desde;
        });
        setHayRemates(hay);
      })
      .catch(() => {});
  }, []);

  if (verificando) return <div style={{ padding: '50px', textAlign: 'center' }}>Iniciando FormoProp...</div>;

  const abrirModalClave = () => {
    setClaveActual(''); setClaveNueva(''); setClaveConfirm('');
    setClaveError(null); setClaveExito(false);
    setModalClave(true);
  };

  const cambiarClave = async (e) => {
    e.preventDefault();
    setClaveError(null);
    if (claveNueva.length < 6) { setClaveError('La nueva contraseña debe tener al menos 6 caracteres.'); return; }
    if (claveNueva !== claveConfirm) { setClaveError('Las contraseñas nuevas no coinciden.'); return; }
    setClaveLoading(true);
    try {
      // Reautenticar primero para evitar el error requires-recent-login
      const credential = EmailAuthProvider.credential(auth.currentUser.email, claveActual);
      await reauthenticateWithCredential(auth.currentUser, credential);
      await updatePassword(auth.currentUser, claveNueva);
      setClaveExito(true);
      setClaveActual(''); setClaveNueva(''); setClaveConfirm('');
    } catch (err) {
      if (err.code === 'auth/wrong-password' || err.code === 'auth/invalid-credential') {
        setClaveError('La contraseña actual es incorrecta.');
      } else {
        setClaveError('Error al cambiar la contraseña. Intentá de nuevo.');
      }
    }
    setClaveLoading(false);
  };

  const panelSegunRol = () => {
    if (!user) return <Navigate to="/login" />;
    if (usuario?.activo === false) return (
      <div style={{ padding: '80px 20px', textAlign: 'center' }}>
        <p style={{ fontSize: '48px', margin: '0 0 16px' }}>🔒</p>
        <h2 style={{ color: '#dc3545', margin: '0 0 12px' }}>Cuenta suspendida</h2>
        <p style={{ color: '#666', fontSize: '15px', maxWidth: '420px', margin: '0 auto 24px' }}>
          Tu cuenta está actualmente inactiva. Contactate con el administrador de FormoProp para más información.
        </p>
        <button onClick={() => signOut(auth)} style={{ padding: '10px 28px', background: '#dc3545', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' }}>
          Cerrar sesión
        </button>
      </div>
    );
    if (usuario?.rol === 'admin')   return <PanelAdmin   user={user} usuario={usuario} />;
    if (usuario?.rol === 'agente')  return <PanelAgente  user={user} usuario={usuario} />;
    return <PanelPropiedades user={user} usuario={usuario} />;
  };

  return (
    <Router>
      <RouteTracker />
      <style>{`
        .fp-nav {
          display:flex; align-items:center; flex-wrap:wrap; gap:8px;
          background:white; padding:8px 28px;
          border-bottom:3px solid #1e3a6e;
          box-shadow:0 2px 10px rgba(30,58,110,0.10);
        }
        .fp-brand { margin-right:auto; }
        .fp-nav-links { display:flex; align-items:center; flex-wrap:wrap; justify-content:flex-end; gap:4px; order:1; }
        .fp-nav-actions { display:flex; align-items:center; flex-wrap:wrap; gap:4px; order:2; }
        .fp-navlink {
          color:#1e3a6e; text-decoration:none; margin-left:12px; font-size:14px; font-weight:600;
          padding:6px 4px; border-bottom:2px solid transparent; transition:border-color 0.15s;
        }
        .fp-navlink:hover { border-bottom-color:#1e3a6e; }
        /* Móvil: Acceso CIF queda arriba junto al logo; los links pasan a una fila propia abajo */
        @media (max-width:600px) {
          .fp-nav-links { order:3; flex-basis:100%; justify-content:center; margin-top:4px; }
        }
        @media (max-width:520px) {
          .fp-nav { padding:8px 14px; }
          .fp-nav-links a, .fp-nav-links button, .fp-nav-actions a, .fp-nav-actions button { font-size:12px !important; padding:5px 8px !important; margin-left:4px !important; }
          .fp-brand-tagline { display:none; }
        }
      `}</style>
      <div style={{ backgroundColor: '#f8f9fa', minHeight: '100vh', fontFamily: 'sans-serif', display: 'flex', flexDirection: 'column' }}>

        {/* ── MODAL CAMBIAR CONTRASEÑA ── */}
        {modalClave && (
          <div style={modalOverlay} onClick={e => { if (e.target === e.currentTarget) setModalClave(false); }}>
            <div style={modalBox}>
              <div style={modalHeader}>
                <h2 style={{ margin: 0, fontSize: '18px' }}>🔑 Cambiar contraseña</h2>
                <button onClick={() => setModalClave(false)} style={btnCerrar}>✕</button>
              </div>
              <div style={modalBody}>
                {claveExito ? (
                  <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <p style={{ fontSize: '40px', margin: '0 0 12px' }}>✅</p>
                    <p style={{ fontWeight: 'bold', fontSize: '16px', color: '#155724', margin: '0 0 8px' }}>¡Contraseña actualizada!</p>
                    <p style={{ color: '#666', fontSize: '14px', margin: '0 0 24px' }}>Tu nueva contraseña ya está activa.</p>
                    <button onClick={() => setModalClave(false)} style={btnPrimary}>Cerrar</button>
                  </div>
                ) : (
                  <form onSubmit={cambiarClave}>
                    <label style={labelMini}>Contraseña actual *</label>
                    <input
                      type="password"
                      value={claveActual}
                      onChange={e => setClaveActual(e.target.value)}
                      style={inputStyle}
                      placeholder="••••••••"
                      required
                      autoComplete="current-password"
                    />
                    <label style={labelMini}>Nueva contraseña * <span style={{ color: '#888', fontWeight: 'normal' }}>(mín. 6 caracteres)</span></label>
                    <input
                      type="password"
                      value={claveNueva}
                      onChange={e => setClaveNueva(e.target.value)}
                      style={inputStyle}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    <label style={labelMini}>Confirmar nueva contraseña *</label>
                    <input
                      type="password"
                      value={claveConfirm}
                      onChange={e => setClaveConfirm(e.target.value)}
                      style={inputStyle}
                      placeholder="••••••••"
                      required
                      autoComplete="new-password"
                    />
                    {claveError && (
                      <p style={{ color: '#dc3545', background: '#fff5f5', border: '1px solid #f5c2c7', borderRadius: '6px', padding: '10px 14px', fontSize: '13px', margin: '4px 0 10px' }}>
                        {claveError}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                      <button type="submit" disabled={claveLoading} style={btnPrimary}>
                        {claveLoading ? 'Actualizando...' : 'Cambiar contraseña'}
                      </button>
                      <button type="button" onClick={() => setModalClave(false)} style={btnCancel}>Cancelar</button>
                    </div>
                  </form>
                )}
              </div>
            </div>
          </div>
        )}

        {/* BARRA SUPERIOR */}
        <nav className="fp-nav">
          <Link to="/" className="fp-brand" style={{ display: 'flex', alignItems: 'center', gap: '10px', textDecoration: 'none', flexShrink: 0 }}>
            <img src="/logo_formoprop.png" alt="FormoProp" style={{ height: '40px', width: 'auto' }} />
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <span style={{ color: '#1e3a6e', fontWeight: '900', fontSize: '22px', letterSpacing: '0.5px' }}>
                Formo<span style={{ color: '#2e6db4' }}>Prop</span>
              </span>
              <span className="fp-brand-tagline" style={{ color: '#7a9cc4', fontSize: '9.5px', fontWeight: '600', letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                Por Profesionales
              </span>
            </div>
          </Link>
          {/* Acciones de usuario / login — quedan arriba junto al logo en móvil */}
          <div className="fp-nav-actions">
            {user ? (
              <>
                {usuario?.rol === 'admin' && (
                  <span style={{ background: '#dc3545', color: 'white', fontSize: '10px', fontWeight: 'bold', padding: '2px 8px', borderRadius: '10px', marginLeft: '6px' }}>
                    ADMIN
                  </span>
                )}
                <Link to="/panel" className="fp-navlink">
                  {usuario?.rol === 'admin' ? 'Panel Admin' : 'Mi Panel'}
                </Link>
                <button onClick={abrirModalClave} style={btnClave} title="Cambiar contraseña">🔑</button>
                <button onClick={() => signOut(auth)} style={btnSalir}>Salir</button>
              </>
            ) : (
              <Link to="/login" style={btnIngresar}>Acceso CIF</Link>
            )}
          </div>
          <div className="fp-nav-links">
            <Link to="/" className="fp-navlink">Catálogo</Link>
            <Link to="/comprar" className="fp-navlink">Comprar</Link>
            <Link to="/vender" className="fp-navlink">Vender</Link>
            {hayRemates && <Link to="/remates" className="fp-navlink">Remates</Link>}
            <Link to="/alquileres" className="fp-navlink">Alquileres</Link>
            <Link to="/nosotros" className="fp-navlink">Nosotros</Link>
            {user && (usuario?.rol === 'corredor' || usuario?.rol === 'admin') && (
              <Link to="/estadisticas" className="fp-navlink">📊 Stats</Link>
            )}
            {user && (usuario?.rol === 'admin' || usuario?.puedeGestionarCuotas) && (
              <Link to="/cuotas" className="fp-navlink">💰 Cuotas</Link>
            )}
          </div>
        </nav>

        {/* CONTENIDO */}
        <div style={{ flex: 1 }}>
          <Suspense fallback={<div style={{ padding: '60px', textAlign: 'center', color: '#9ca3af' }}>Cargando…</div>}>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/nosotros" element={<QuienesSomos />} />
            <Route path="/propiedad/:id" element={<DetallePropiedad />} />
            <Route path="/remates" element={<Remates />} />
            <Route path="/remate/:id" element={<RemateDetalle />} />
            <Route path="/corredor/:id" element={<CorredorDetalle />} />
            <Route path="/estadisticas/:id" element={user ? <Estadisticas user={user} usuario={usuario} /> : <Navigate to="/login" />} />
            <Route path="/estadisticas" element={user ? <EstadisticasGenerales user={user} usuario={usuario} /> : <Navigate to="/login" />} />
            <Route path="/estadisticas-propiedad/:id" element={<EstadisticasPropietario />} />
            <Route path="/comprar" element={<ComprarPropiedad />} />
            <Route path="/cancelar-busqueda" element={<CancelarBusqueda />} />
            <Route path="/cancelar-remates" element={<CancelarRemates />} />
            <Route path="/vender" element={<VenderPropiedad />} />
            <Route path="/alquileres" element={<Alquileres />} />
            <Route path="/calculadora" element={<CalculadoraAlquileres />} />
            <Route path="/calculadora-alquileres" element={<Navigate to="/calculadora" />} />
            <Route path="/login" element={user ? <Navigate to="/panel" /> : <Login />} />
            <Route path="/panel" element={panelSegunRol()} />
            <Route path="/cuotas" element={
              user && (usuario?.rol === 'admin' || usuario?.puedeGestionarCuotas)
                ? <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '30px 20px' }}><h2 style={{ margin: '0 0 20px', color: '#1e3a6e' }}>💰 Cuotas de Socios</h2><GestionCuotas user={user} /></div>
                : <Navigate to="/login" />
            } />
            <Route path="/validar-importacion" element={<ValidarImportacion />} />
            {/* URL amigable del corredor (/:slug) — debe ir SIEMPRE al final:
                React Router prioriza las rutas estáticas de arriba sobre esta dinámica. */}
            <Route path="/:slug" element={<CorredorDetalle />} />
          </Routes>
          </Suspense>
        </div>

        {/* FOOTER */}
        <footer style={footerStyle}>
          <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
            <div>
              <h2 style={{ margin: 0, fontSize: '20px', color: 'white' }}>FormoProp</h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', color: '#aaa' }}>{config.footerSubtitulo}</p>
              <a href="https://web.facebook.com/formoprop" target="_blank" rel="noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', marginTop: '10px', color: '#4a90d9', textDecoration: 'none', fontSize: '13px', fontWeight: 'bold' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="#4a90d9"><path d="M24 12.073C24 5.405 18.627 0 12 0S0 5.405 0 12.073C0 18.1 4.388 23.094 10.125 24v-8.437H7.078v-3.49h3.047V9.413c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.234 2.686.234v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.49h-2.796V24C19.612 23.094 24 18.1 24 12.073z"/></svg>
                Seguinos en Facebook
              </a>
            </div>
            <div style={{ textAlign: 'right' }}>
              <p style={{ margin: 0, fontSize: '14px', color: '#ccc' }}>
                Con el respaldo institucional de la
                <a href="http://www.cif.org.ar" target="_blank" rel="noreferrer" style={{ color: '#007bff', textDecoration: 'none', fontWeight: 'bold', marginLeft: '5px' }}>CIF</a>
              </p>
              <p style={{ margin: '5px 0 0 0', fontSize: '12px', color: '#888' }}>Cámara Inmobiliaria de Formosa</p>
            </div>
          </div>

          {/* DISCLAIMER LEGAL */}
          <div style={{ maxWidth: '1200px', margin: '20px auto 0', borderTop: '1px solid #2a2a2a', paddingTop: '18px' }}>
            <p style={{ margin: '0 0 6px', fontSize: '11px', color: '#666', fontWeight: 'bold', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              Acerca de nuestras publicaciones
            </p>
            <p style={{ margin: 0, fontSize: '11px', color: '#555', lineHeight: '1.6' }}>
              {config.footerDisclaimer}
            </p>
          </div>
        </footer>
      </div>
    </Router>
  );
}

const navLink = { color: '#1e3a6e', textDecoration: 'none', marginLeft: '14px', fontSize: '14px', fontWeight: '600' };
const btnIngresar = { color: 'white', textDecoration: 'none', marginLeft: '14px', fontSize: '14px', fontWeight: '700', background: '#1e3a6e', padding: '8px 16px', borderRadius: '7px', letterSpacing: '0.2px' };
const btnSalir = { background: 'none', border: '1px solid #e53935', color: '#e53935', padding: '6px 12px', marginLeft: '8px', cursor: 'pointer', borderRadius: '5px', fontWeight: 'bold', fontSize: '13px' };
const btnClave = { background: 'none', border: '1px solid #c8d6e8', color: '#5a7fa8', padding: '5px 9px', marginLeft: '8px', cursor: 'pointer', borderRadius: '5px', fontSize: '14px' };
const footerStyle = { background: '#111', padding: '30px 20px', borderTop: '1px solid #333', marginTop: '40px' };

// Modal styles
const modalOverlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const modalBox = { background: 'white', borderRadius: '14px', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' };
const modalHeader = { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '20px 24px', borderBottom: '1px solid #eee' };
const modalBody = { padding: '24px' };
const btnCerrar = { background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#888', lineHeight: 1 };
const inputStyle = { width: '100%', padding: '11px 13px', marginBottom: '12px', borderRadius: '6px', border: '1px solid #ddd', boxSizing: 'border-box', fontSize: '14px' };
const labelMini = { display: 'block', fontSize: '12px', fontWeight: 'bold', color: '#555', marginBottom: '4px' };
const btnPrimary = { flex: 1, padding: '12px', background: '#1a1a1a', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontWeight: 'bold', fontSize: '14px' };
const btnCancel = { padding: '12px 20px', background: '#6c757d', color: 'white', border: 'none', borderRadius: '7px', cursor: 'pointer', fontSize: '14px' };
